import { Card } from "../components/ui/card.jsx";
import { useEffect, useState } from "react";
import HunterJourneyMindMap from "../components/HunterJourneyMindMap.jsx";
import HunterJourneyLadder from "../components/HunterJourneyLadder.jsx";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function GearPrep() {
  const [activeTab, setActiveTab] = useState('journey');

  const essentialGearSections = [
    {
      key: 'legal-nav',
      title: '📋 Legal & Navigation Requirements',
      icon: '🗺️',
      bullets: [
        'Valid hunting license and ID - verify current status before season',
        'Species-specific game tags - pre-purchase required tags for big game',
        'Hunter education certificate - mandatory for hunters born after 1975 (varies by state)',
        'GPS device with offline maps (onX Hunt or similar) - don\'t rely on cell signal',
        'Physical topographic map and quality compass - technology fails, paper doesn\'t',
        'Bright orange marking tape - mark your entry/exit route for safe navigation back',
      ],
    },
    {
      key: 'hunter-orange',
      title: '🟠 Blaze Orange Safety Requirements',
      icon: '🦺',
      bullets: [
        'Blaze orange vest - minimum 500 square inches visible from all sides',
        'Blaze orange hat or headwear - increases visibility to other hunters',
        'Check state-specific requirements - some states mandate orange during rifle seasons only',
        'Waterfowl exception - orange not required in wetlands/marshes in most states',
        'Archery seasons - orange requirements often reduced or waived, verify locally',
        'Purpose: prevents hunting accidents by making you visible to other hunters',
      ],
    },
    {
      key: 'weapons-optics',
      title: '🎯 Weapons, Optics & Ammunition',
      icon: '🔭',
      bullets: [
        'Firearm or bow - properly maintained, legal for species, zeroed before season',
        'Adequate ammunition or arrows - bring extra beyond what you think you\'ll need',
        'Quality rangefinder - practice quick target acquisition at expected distances',
        'Binoculars - quality optics matter more than magnification power',
        'Scope with clear glass - verify zero at multiple distances, check for fog/water intrusion',
        'Weapon sling or carrying case - hands-free transport reduces fatigue and accidents',
        'Cleaning kit - clear barrel obstructions immediately; even small blockages are dangerous',
      ],
    },
    {
      key: 'field-processing',
      title: '🔪 Field Processing Essentials',
      icon: '🛠️',
      bullets: [
        'Sharp fixed-blade hunting knife (4-5 inch) - dedicated field dressing blade',
        'Folding knife or replaceable-blade knife - backup cutting tool',
        'Knife sharpener - dull knives are dangerous and make processing difficult',
        'Nitrile or latex gloves (multiple pairs) - hygiene during field dressing',
        'Game bags (breathable mesh) - protect meat from flies and contamination',
        'Large garbage bags or construction tarps - ground cloth for clean processing area',
        'Bone saw or limb loppers - for larger game carcass management',
        'Paracord or rope - for hanging/dragging game',
      ],
    },
    {
      key: 'pack-gear',
      title: '🎒 Hunting Pack & Accessories',
      icon: '🧳',
      bullets: [
        'Quality hunting backpack - comfortable frame, multiple compartments, quiet fabric',
        'Portable seat cushion or chair - comfort during long sits increases success',
        'Species-specific calls and decoys - practice before season; master 3-4 basic sounds',
        'Scent eliminator spray - start scent control at home with unscented detergent',
        'Wind checker - constantly monitor wind direction; scent strategy is critical',
        'Headlamp with red light mode - preserves night vision, hands-free operation',
        'Extra batteries - cold kills batteries fast; carry spares for all electronic devices',
      ],
    },
  ];

  const layeringSystem = [
    {
      layer: 'Layer 1: Base Layer',
      title: 'Moisture Management Foundation',
      purpose: 'Wicks sweat away from skin to prevent hypothermia',
      materials: [
        'Merino wool - naturally odor-resistant, warm when wet, comfortable (premium option)',
        'Synthetic polyester/spandex - quick-drying, durable, affordable (retains odors more than wool)',
        'Long-sleeve shirt and bottoms for temperatures below 70°F',
        'Expedition-weight merino for extreme cold (below 20°F)',
      ],
      tips: 'Tuck in your base layer to prevent cold air from reaching skin. Avoid cotton - it retains moisture and will chill you.',
    },
    {
      layer: 'Layer 2: Mid-Layer',
      title: 'Insulation & Heat Retention',
      purpose: 'Traps body heat while wicking moisture from base layer',
      materials: [
        'Fleece sweaters or jackets - breathable warmth',
        'Merino wool vest - core warmth without bulk',
        'Synthetic insulation (Primaloft) - maintains warmth when wet',
        'Down or synthetic puffy vests - packable, excellent warmth-to-weight ratio',
      ],
      tips: 'Most dynamic layer - add when stationary (glassing/stand hunting), remove during heavy activity (hiking uphill) to avoid overheating and sweat.',
    },
    {
      layer: 'Layer 3: Outer Shell',
      title: 'Wind & Water Protection',
      purpose: 'Blocks wind and light rain while allowing moisture to escape',
      materials: [
        'Softshell jacket and pants - quiet, flexible, breathable',
        'DWR (Durable Water Repellent) treated fabric - sheds light precipitation',
        'Underarm zippered vents - critical for temperature regulation during activity',
        'Quiet fabric is essential - noisy synthetic shells spook game',
      ],
      tips: 'Choose earth-tone or camouflage patterns. Ensure shell layer doesn\'t restrict movement when drawing bow or shouldering rifle.',
    },
    {
      layer: 'Layer 4: Insulation Layer',
      title: 'Extreme Cold Protection',
      purpose: 'Maximum warmth for stationary hunting in frigid temperatures',
      materials: [
        'Down jacket or vest - exceptional warmth-to-weight ratio, compressible',
        'Synthetic insulated jackets - maintains warmth even when wet',
        'Packable in backpack - wear when sitting, remove when active',
      ],
      tips: 'Think of this like a mobile sleeping bag. Only wear when completely stationary. Remove immediately when starting to move to prevent sweating.',
    },
    {
      layer: 'Layer 5: Hardshell / Rain Gear',
      title: 'Ultimate Weather Barrier',
      purpose: '100% waterproof protection against rain, snow, and wind',
      materials: [
        'Waterproof, breathable fabric (Gore-Tex or equivalent)',
        'Fully taped seams - no water intrusion points',
        'Adjustable hood - protection without restricting vision or hearing',
        'Stowable in pack until needed',
      ],
      tips: 'Keep in pack until weather turns. Can cause overheating if worn unnecessarily. Pair with moisture-wicking layers underneath.',
    },
  ];

  const survivalGear = [
    {
      category: 'Fire & Warmth',
      items: [
        'Waterproof fire starter kit - cotton balls with Vaseline, fatwood, or storm matches',
        'BIC lighter in waterproof case - most reliable fire source',
        'Ferro rod - works in any weather, thousands of strikes',
        'Emergency space blanket - lightweight, reflective, prevents hypothermia',
        'Hand and toe warmers - disposable heat packs for extreme cold',
        'Extra dry socks and base layer - wet clothing is dangerous in cold weather',
      ],
    },
    {
      category: 'First Aid & Medical',
      items: [
        'CAT tourniquet - critical for severe bleeding control, practice application',
        'Pressure bandages and gauze - wound management',
        'SAM splint - for fractures and sprains',
        'Band-aids and antibiotic cream - minor injury treatment',
        'Pain relief medication (ibuprofen/acetaminophen)',
        'Emergency whistle - three blasts signals distress',
        'Personal medications - always carry extra doses',
      ],
    },
    {
      category: 'Shelter & Protection',
      items: [
        'Emergency tent or small tarp - lightweight shelter option',
        'Paracord (50+ feet) - shelter building, gear repair, game processing',
        'Duct tape - repairs clothing, gear, creates splints',
        'Bright orange surveyor tape - mark location for rescuers',
      ],
    },
    {
      category: 'Water & Food',
      items: [
        'Water bottle or hydration bladder - carry more than you think you need',
        'Water purification tablets - backup if you\'re stranded',
        'UV water filter (optional) - lightweight purification for extended trips',
        'High-energy snacks - trail mix, jerky, energy bars, nut butter packets',
        'Electrolyte tablets - prevent dehydration and cramping',
      ],
    },
    {
      category: 'Tools & Communication',
      items: [
        'Multi-tool - knife, pliers, screwdrivers, saw, wire cutter, can opener',
        'Cell phone in waterproof case - emergency communication and photos',
        'Portable battery power bank - keep devices charged',
        'Personal Locator Beacon (PLB) - GPS emergency rescue signal for remote areas',
        'Signal mirror - visible for miles in sunlight',
        'Headlamp with SOS mode - nighttime signaling',
      ],
    },
  ];

  const firearmSafety = [
    {
      title: 'The Four Fundamental Rules (TAB-K)',
      rules: [
        {
          letter: 'T',
          rule: 'Treat every firearm as if it is loaded',
          detail: 'Check the chamber every single time you pick up a firearm - even if you just watched someone unload it. Most accidents happen with "unloaded" guns.',
        },
        {
          letter: 'A',
          rule: 'Always point the muzzle in a safe direction',
          detail: 'A safe direction means if the gun discharged, it would not cause injury or damage. Never point at anything you don\'t intend to shoot.',
        },
        {
          letter: 'B',
          rule: 'Be sure of your target and what\'s beyond it',
          detail: 'Positively identify your target - never shoot at sounds or movement. Bullets travel far beyond your target. Know your backstop.',
        },
        {
          letter: 'K',
          rule: 'Keep your finger outside the trigger guard until ready to shoot',
          detail: 'Finger stays outside the trigger guard until you\'re aimed at your target and have made the decision to fire. Don\'t rely on the safety.',
        },
      ],
    },
    {
      title: 'Critical Field Safety Practices',
      practices: [
        'Unload before entering or exiting vehicles - most accidents occur during loading/unloading',
        'Never climb trees, fences, or obstacles with a loaded firearm',
        'Use correct ammunition - match caliber/gauge stamped on barrel exactly',
        'Keep barrel clear of obstructions - snow, mud, or debris can cause barrel rupture',
        'Never shoot at hard surfaces (water, rocks) - ricochets are unpredictable',
        'Use binoculars for identification - never use your scope to identify targets',
        'Know your safe zone-of-fire - the specific area where you can safely discharge',
        'Wear eye and ear protection - shooting causes permanent hearing damage',
      ],
    },
    {
      title: 'Transportation Rules',
      points: [
        'Unload firearms completely before placing in vehicles',
        'Store in locked, hard-sided case in trunk or area inaccessible from passenger compartment',
        'Transport ammunition separately when possible',
        'Know state-specific laws - requirements vary significantly between states',
        'For interstate travel: firearm must be legal in origin AND destination states',
        'Air travel: declare at ticket counter, checked baggage only, locked hard case required',
      ],
    },
    {
      title: 'Storage Best Practices',
      storage: [
        'Unload all firearms when not in use',
        'Store in locked gun safe or with trigger locks installed',
        'Keep ammunition in separate locked location',
        'Ensure firearms are inaccessible to unauthorized persons (children, visitors)',
        'Clean after every use to prevent malfunctions and obstructions',
        'Perform annual inspection of all mechanical components',
      ],
    },
  ];

  const treestandSafety = [
    {
      stat: '1 in 3',
      detail: 'hunters who use elevated stands will have a fall resulting in serious injury',
      color: 'bg-red-500/20 border-red-500/40',
    },
    {
      stat: '82%',
      detail: 'of treestand fall victims were NOT using a fall arrest system',
      color: 'bg-orange-500/20 border-orange-500/40',
    },
    {
      stat: '<12 inches',
      detail: 'maximum fall distance when properly using a full-body harness system',
      color: 'bg-emerald-500/20 border-emerald-500/40',
    },
  ];

  const treestandRules = [
    {
      title: 'Always Use Full-Body Harness (Fall Arrest System)',
      points: [
        'Attach harness to tree at ground level before climbing',
        'Stay connected throughout entire hunt - climbing up, sitting, descending',
        'Free harness included with every new treestand (required by manufacturer)',
        'Quality after-market harnesses available for under $100',
        'Practice using harness and suspension relief strap at ground level before hunting',
        'Inspect harness before each use - check for wear, damage, and expiration date',
      ],
    },
    {
      title: 'Three-Point Contact Rule',
      points: [
        'Always maintain three points of contact when climbing: two hands + one foot OR two feet + one hand',
        'Never climb with anything in your hands or on your back',
        'Use haul line to pull up unloaded firearm/bow and gear after you\'re secured',
        'Climb slowly and deliberately - no rushing',
      ],
    },
    {
      title: 'Suspension Trauma Prevention',
      points: [
        'Even if harness catches you, blood can pool in legs causing unconsciousness',
        'Modern harnesses include suspension relief straps - learn to use them',
        'These straps create a step to stand on, relieving pressure and maintaining blood flow',
        'Practice deploying relief strap at ground level - in emergency you must do it quickly',
      ],
    },
    {
      title: 'Emergency Preparedness',
      points: [
        'Carry cell phone, whistle, signal flare, and flashlight on your person (not in pack)',
        'Tell someone exact hunting location and expected return time',
        'Recommended stand height: less than 10 feet above ground',
        'Annual inspection: check all metal connections, fabric wear, and tree health',
        'Retire questionable gear immediately - your life depends on it',
      ],
    },
  ];

  const tabs = [
    { id: 'journey', label: 'Hunter Journey', icon: '🎯' },
    { id: 'essentials', label: 'Essential Gear', icon: '🎒' },
    { id: 'layering', label: 'Clothing Layers', icon: '🧥' },
    { id: 'survival', label: 'Survival Kit', icon: '⚡' },
    { id: 'firearms', label: 'Firearm Safety', icon: '🔫' },
    { id: 'treestand', label: 'Treestand Safety', icon: '🌳' },
  ];

  return (
    <div className="container mx-auto px-6 py-10" data-testid="gear-prep-page">
      <h1 className="text-3xl md:text-4xl font-bold font-serif text-white mb-3" data-testid="gear-prep-title">
        Gear & Preparation Guide
      </h1>
      <p className="text-white/70 text-sm max-w-4xl" data-testid="gear-prep-subtitle">
        Comprehensive hunter education essentials covering gear selection, safety protocols, and field preparation. Based on current best practices from state wildlife agencies, hunter education programs, and safety organizations. Always verify equipment requirements and hunting methods with your local regulations before heading afield.
      </p>

      {/* Hunter's Code Foundation */}
      <Card className="p-6 mt-8 hh-card text-white border-l-4 border-l-emerald-500" data-testid="gear-safety-extract">
        <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
          🛡️ Foundation of Safe & Ethical Hunting
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h3 className="font-bold text-lg text-emerald-400 mb-3">Firearm Safety Fundamentals</h3>
            <ul className="space-y-2 text-sm text-white/90">
              <li className="flex gap-2"><span className="text-emerald-400">•</span> <span><strong>Muzzle control:</strong> Always point in safe direction - if it discharged, no injury or damage would occur</span></li>
              <li className="flex gap-2"><span className="text-emerald-400">•</span> <span><strong>Target certainty:</strong> Positively identify your target AND what's beyond it - never shoot at sounds or movement</span></li>
              <li className="flex gap-2"><span className="text-emerald-400">•</span> <span><strong>Finger discipline:</strong> Keep finger outside trigger guard until aimed and ready to fire</span></li>
              <li className="flex gap-2"><span className="text-emerald-400">•</span> <span><strong>Treat as loaded:</strong> Check chamber every time you pick up firearm, even if you just saw it unloaded</span></li>
              <li className="flex gap-2"><span className="text-emerald-400">•</span> <span><strong>Ammunition accuracy:</strong> Match caliber/gauge exactly to barrel markings - wrong ammo can be fatal</span></li>
            </ul>
          </div>
          <div>
            <h3 className="font-bold text-lg text-emerald-400 mb-3">Ethical Standards & Conservation</h3>
            <ul className="space-y-2 text-sm text-white/90">
              <li className="flex gap-2"><span className="text-emerald-400">•</span> <span><strong>Land respect:</strong> Always secure written permission; leave gates as found; report property damage</span></li>
              <li className="flex gap-2"><span className="text-emerald-400">•</span> <span><strong>Fair chase principles:</strong> Pursue game in natural, free-roaming state - no unfair advantage methods</span></li>
              <li className="flex gap-2"><span className="text-emerald-400">•</span> <span><strong>Clean harvest:</strong> Practice marksmanship year-round; aim for quick, humane kills within effective range</span></li>
              <li className="flex gap-2"><span className="text-emerald-400">•</span> <span><strong>Conservation mindset:</strong> Strictly follow bag limits; report violations; support habitat conservation</span></li>
              <li className="flex gap-2"><span className="text-emerald-400">•</span> <span><strong>Respect wildlife:</strong> Use all edible meat; minimize waste; honor the animal that gives its life</span></li>
            </ul>
          </div>
        </div>
      </Card>

      {/* Visual Gear Examples */}
      <div className="grid md:grid-cols-3 gap-4 mt-8">
        <Card className="overflow-hidden hh-card">
          <img 
            src="/images/gear_images/safety_gear_v2.jpg"
            alt="Hunter Safety Equipment and Survival Gear"
            className="w-full h-48 object-cover"
          />
          <div className="p-4 text-white">
            <h3 className="font-bold mb-1">Safety Equipment</h3>
            <p className="text-sm text-white/70">First aid, emergency gear, and survival essentials for backcountry hunts</p>
          </div>
        </Card>
        <Card className="overflow-hidden hh-card">
          <img 
            src="/images/gear_images/hunter_layers_v2.jpg"
            alt="Hunting Clothing Layering System"
            className="w-full h-48 object-cover"
          />
          <div className="p-4 text-white">
            <h3 className="font-bold mb-1">Layering System</h3>
            <p className="text-sm text-white/70">Proper clothing layers regulate temperature and keep you hunting longer</p>
          </div>
        </Card>
        <Card className="overflow-hidden hh-card">
          <img 
            src="/images/gear_images/firearm_storage_safety.jpg"
            alt="Safe Firearm Storage and Organization"
            className="w-full h-48 object-cover"
          />
          <div className="p-4 text-white">
            <h3 className="font-bold mb-1">Firearm Safety</h3>
            <p className="text-sm text-white/70">Safe handling, transportation, and storage prevent accidents</p>
          </div>
        </Card>
      </div>

      {/* Tab Navigation */}
      <div className="flex flex-wrap gap-2 mt-10 mb-6">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-full font-medium transition-all ${
              activeTab === tab.id
                ? 'bg-emerald-500 text-white shadow-lg'
                : 'bg-white/5 text-white/70 hover:bg-white/10'
            }`}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Combined Hunter Journey Tab */}
      {activeTab === 'journey' && (
        <div className="space-y-6">
          {/* Journey Overview */}
          <Card className="p-6 hh-card text-white border-l-4 border-l-emerald-500">
            <h2 className="text-2xl font-bold mb-4">🎯 Your Complete Path to Becoming a Hunter</h2>
            <p className="text-white/90 mb-4">
              Starting your hunting journey is exciting but can feel overwhelming. This interactive roadmap breaks down the process into clear, manageable phases. 
              Explore both visualizations below - the journey map shows the progression flow, while the ladder climb illustrates how each phase builds upon the previous one.
            </p>
            <div className="grid md:grid-cols-3 gap-4 text-sm">
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-emerald-400 mb-1">0-3</div>
                <p className="text-white/80">Months to be ready for first hunt</p>
              </div>
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-blue-400 mb-1">$500-$700</div>
                <p className="text-white/80">Minimum initial investment to start</p>
              </div>
              <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-orange-400 mb-1">70%</div>
                <p className="text-white/80">Success rate with mentorship</p>
              </div>
            </div>
          </Card>

          {/* Ladder Climb Visualization */}
          <HunterJourneyLadder />
        </div>
      )}

      {/* Essential Gear Checklist Tab */}
      {activeTab === 'essentials' && (
        <div className="grid md:grid-cols-2 gap-5" data-testid="gear-prep-grid">
          {essentialGearSections.map((section) => (
            <Card key={section.key} className="p-5 hh-card text-white" data-testid={`gear-prep-card-${section.key}`}>
              <h2 className="text-xl font-bold mb-3">{section.title}</h2>
              <ul className="space-y-2">
                {section.bullets.map((bullet, i) => (
                  <li key={i} className="flex gap-2 text-sm text-white/85">
                    <span className="text-emerald-400 mt-1">•</span>
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
            </Card>
          ))}
        </div>
      )}

      {/* Clothing Layering System Tab */}
      {activeTab === 'layering' && (
        <div className="space-y-5">
          <Card className="p-6 hh-card text-white border-l-4 border-l-blue-500">
            <h2 className="text-2xl font-bold mb-3">The 5-Layer System</h2>
            <p className="text-white/80 mb-4">
              Proper layering regulates body temperature by trapping still air (an excellent insulator), wicking moisture away from skin, and adapting to changing weather and activity levels. The goal: <strong>stay comfortable, stay hunting.</strong>
            </p>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <h3 className="font-bold text-blue-300 mb-2">✓ Layering Best Practices</h3>
                <ul className="space-y-1 text-white/80">
                  <li>• "Be bold, start cold" - begin with lighter layers</li>
                  <li>• Pack extra layers - weather changes quickly</li>
                  <li>• Avoid cotton - use merino wool or synthetics</li>
                  <li>• Adjust constantly: add when stationary, remove when active</li>
                  <li>• Change socks regularly to prevent blisters</li>
                </ul>
              </div>
              <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4">
                <h3 className="font-bold text-orange-300 mb-2">✗ Common Mistakes</h3>
                <ul className="space-y-1 text-white/80">
                  <li>• Wearing cotton base layers (retains moisture, chills you)</li>
                  <li>• Starting too warm (causes overheating and sweat)</li>
                  <li>• Not removing layers during activity (leads to hypothermia)</li>
                  <li>• Choosing noisy shell fabrics (spooks game)</li>
                  <li>• Forgetting extra socks (wet feet = cold feet)</li>
                </ul>
              </div>
            </div>
          </Card>
          
          {layeringSystem.map((layer, idx) => (
            <Card key={idx} className="p-5 hh-card text-white">
              <div className="flex items-start gap-3 mb-3">
                <div className="bg-emerald-500/20 text-emerald-400 rounded-full w-10 h-10 flex items-center justify-center font-bold text-lg shrink-0">
                  {idx + 1}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-emerald-400">{layer.layer}</h3>
                  <p className="text-lg font-semibold mt-1">{layer.title}</p>
                </div>
              </div>
              <div className="bg-white/5 rounded-lg p-4 mb-3">
                <p className="text-sm font-medium text-white/90"><strong className="text-emerald-400">Purpose:</strong> {layer.purpose}</p>
              </div>
              <h4 className="font-bold text-sm mb-2 text-white/90">Materials & Options:</h4>
              <ul className="space-y-1 mb-3">
                {layer.materials.map((material, i) => (
                  <li key={i} className="flex gap-2 text-sm text-white/80">
                    <span className="text-emerald-400">•</span>
                    <span>{material}</span>
                  </li>
                ))}
              </ul>
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                <p className="text-sm text-white/90"><strong className="text-blue-300">Pro Tip:</strong> {layer.tips}</p>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Survival Kit Tab */}
      {activeTab === 'survival' && (
        <div className="space-y-5">
          <Card className="p-6 hh-card text-white border-l-4 border-l-orange-500">
            <h2 className="text-2xl font-bold mb-3">⚡ Emergency Preparedness Philosophy</h2>
            <p className="text-white/90 mb-4">
              Hunting takes you into remote areas where help may be hours or days away. A well-prepared survival kit isn't paranoia - it's insurance. Even on short day hunts near civilization, weather can change, injuries can occur, and getting lost is easier than you think. Pack for the "what-ifs" so you can focus on the hunt.
            </p>
            <div className="grid md:grid-cols-3 gap-4 text-sm">
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-red-400 mb-1">72 hours</div>
                <p className="text-white/80">Average time lost hunters are found if they stay put and signal</p>
              </div>
              <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-orange-400 mb-1">3 hours</div>
                <p className="text-white/80">Survival time in extreme cold without shelter and fire</p>
              </div>
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-yellow-400 mb-1">Rule of 3</div>
                <p className="text-white/80">3 minutes without air, 3 hours without shelter, 3 days without water, 3 weeks without food</p>
              </div>
            </div>
          </Card>

          {survivalGear.map((category, idx) => (
            <Card key={idx} className="p-5 hh-card text-white">
              <h3 className="text-xl font-bold mb-3 text-orange-400">{category.category}</h3>
              <ul className="grid md:grid-cols-2 gap-x-6 gap-y-2">
                {category.items.map((item, i) => (
                  <li key={i} className="flex gap-2 text-sm text-white/85">
                    <span className="text-orange-400 mt-1">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </Card>
          ))}

          <Card className="p-5 hh-card text-white bg-emerald-500/10 border-2 border-emerald-500/30">
            <h3 className="text-lg font-bold mb-3 text-emerald-400">📱 Emergency Action Plan</h3>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div>
                <h4 className="font-bold mb-2">Before You Go:</h4>
                <ul className="space-y-1 text-white/90">
                  <li>✓ Tell someone your exact hunting location</li>
                  <li>✓ Share your expected return time</li>
                  <li>✓ Describe your vehicle and where it's parked</li>
                  <li>✓ Charge cell phone and power bank fully</li>
                  <li>✓ Check weather forecast for unexpected changes</li>
                </ul>
              </div>
              <div>
                <h4 className="font-bold mb-2">If You Get Lost:</h4>
                <ul className="space-y-1 text-white/90">
                  <li>✓ STOP - Stay calm, don't panic</li>
                  <li>✓ Stay put - rescuers find stationary targets faster</li>
                  <li>✓ Signal - whistle (3 blasts), mirror, bright tape</li>
                  <li>✓ Shelter - get out of wind/rain immediately</li>
                  <li>✓ Fire - warmth and visibility for rescuers</li>
                </ul>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Firearms Safety Tab */}
      {activeTab === 'firearms' && (
        <div className="space-y-5">
          {firearmSafety.map((section, idx) => (
            <Card key={idx} className="p-6 hh-card text-white">
              <h2 className="text-2xl font-bold mb-4 text-red-400">{section.title}</h2>
              
              {section.rules && (
                <div className="space-y-4">
                  {section.rules.map((rule, i) => (
                    <div key={i} className="bg-white/5 rounded-lg p-4 border-l-4 border-l-red-500">
                      <div className="flex items-start gap-3">
                        <div className="bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold shrink-0">
                          {rule.letter}
                        </div>
                        <div className="flex-1">
                          <h3 className="font-bold text-lg mb-1">{rule.rule}</h3>
                          <p className="text-sm text-white/80">{rule.detail}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {section.practices && (
                <ul className="space-y-2">
                  {section.practices.map((practice, i) => (
                    <li key={i} className="flex gap-2 text-sm text-white/85">
                      <span className="text-red-400 mt-1">•</span>
                      <span>{practice}</span>
                    </li>
                  ))}
                </ul>
              )}

              {section.points && (
                <ul className="space-y-2">
                  {section.points.map((point, i) => (
                    <li key={i} className="flex gap-2 text-sm text-white/85">
                      <span className="text-red-400 mt-1">•</span>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              )}

              {section.storage && (
                <ul className="space-y-2">
                  {section.storage.map((item, i) => (
                    <li key={i} className="flex gap-2 text-sm text-white/85">
                      <span className="text-red-400 mt-1">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Treestand Safety Tab */}
      {activeTab === 'treestand' && (
        <div className="space-y-5">
          <Card className="p-6 hh-card text-white border-l-4 border-l-yellow-500">
            <h2 className="text-2xl font-bold mb-4">🌳 Treestand Safety: Critical Statistics</h2>
            <p className="text-white/90 mb-6">
              Treestand accidents are one of the leading causes of hunting injuries. Most are completely preventable with proper equipment and discipline. Here's what you need to know:
            </p>
            <div className="grid md:grid-cols-3 gap-4">
              {treestandSafety.map((stat, idx) => (
                <div key={idx} className={`${stat.color} border rounded-lg p-4 text-center`}>
                  <div className="text-3xl font-bold mb-2">{stat.stat}</div>
                  <p className="text-sm text-white/90">{stat.detail}</p>
                </div>
              ))}
            </div>
          </Card>

          <Card className="overflow-hidden hh-card">
            <img 
              src="/images/gear_images/treestand_safety_har_ff67b646.jpg"
              alt="Treestand Safety Harness System"
              className="w-full h-64 object-cover"
            />
            <div className="p-6 text-white">
              <h3 className="text-xl font-bold mb-2">Full-Body Harness: Your Lifeline</h3>
              <p className="text-white/80 text-sm">
                A properly fitted full-body harness system is mandatory for treestand hunting. Free harnesses are included with new stands, and quality after-market options are available for under $100. Practice using your harness and suspension relief straps at ground level before the season.
              </p>
            </div>
          </Card>

          {treestandRules.map((section, idx) => (
            <Card key={idx} className="p-5 hh-card text-white">
              <h3 className="text-xl font-bold mb-3 text-yellow-400">{section.title}</h3>
              <ul className="space-y-2">
                {section.points.map((point, i) => (
                  <li key={i} className="flex gap-2 text-sm text-white/85">
                    <span className="text-yellow-400 mt-1">•</span>
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </Card>
          ))}

          <Card className="p-6 hh-card text-white bg-red-500/10 border-2 border-red-500/30">
            <h3 className="text-xl font-bold mb-3 text-red-400">⚠️ Final Warning</h3>
            <p className="text-white/90">
              If you're not willing to use a full-body harness 100% of the time - from the moment you leave the ground until you're back down - then you have only two safe options: <strong>stay on the ground or don't hunt.</strong> Treestand falls cause permanent paralysis and death. No trophy is worth your life or mobility. Make the commitment to safety, every single time.
            </p>
          </Card>
        </div>
      )}

      {/* Footer Resources */}
      <Card className="p-6 mt-10 hh-card text-white bg-white/5">
        <h3 className="text-lg font-bold mb-3">📚 Additional Resources</h3>
        <div className="grid md:grid-cols-2 gap-4 text-sm text-white/80">
          <div>
            <h4 className="font-bold text-white mb-2">Hunter Education:</h4>
            <ul className="space-y-1">
              <li>• State fish & wildlife agency websites</li>
              <li>• Hunter-Ed.com - online courses</li>
              <li>• National Shooting Sports Foundation (NSSF)</li>
              <li>• Quality Deer Management Association (QDMA)</li>
            </ul>
          </div>
          <div>
            <h4 className="font-bold text-white mb-2">Safety Organizations:</h4>
            <ul className="space-y-1">
              <li>• Treestand Manufacturers Association (TMA)</li>
              <li>• National Rifle Association (NRA) - safety courses</li>
              <li>• American Red Cross - wilderness first aid</li>
              <li>• Leave No Trace Center for Outdoor Ethics</li>
            </ul>
          </div>
        </div>
      </Card>
    </div>
  );
}
